import { useEffect, useState } from 'react';

type VideoMetadata = {
    aspectRatio: number;
    thumbnailSrc: string;
    thumbnailHeight: number;
    thumbnailWidth: number;
    title: string;
};

type UseVideoMetadata = {
    metadata: VideoMetadata | null;
    loading: boolean;
};

type NoEmbedResponse = {
    title?: string;
    thumbnail_url: string;
    thumbnail_height: number;
    thumbnail_width: number;
    width: number;
    height: number;
};

const requestCache: { [key: string]: Promise<NoEmbedResponse> } = {};

export const fileSourcePattern = /\.(mp4|webm|ogg|mov)(\?.*)?$/i;

const fileRequestCache: { [key: string]: Promise<NoEmbedResponse> } = {};

const readFileMetadata = (src: string): Promise<NoEmbedResponse> => {
    if (!fileRequestCache[src]) {
        fileRequestCache[src] = new Promise((resolve, reject) => {
            const video = document.createElement('video');

            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                resolve({
                    thumbnail_url: '',
                    thumbnail_height: 0,
                    thumbnail_width: 0,
                    width: video.videoWidth,
                    height: video.videoHeight,
                });
                video.removeAttribute('src');
                video.load();
            };
            video.onerror = () => reject(new Error(`Could not read video metadata: ${src}`));
            video.src = src;
        });
    }

    return fileRequestCache[src];
};

export const fetchVideoMetadata = async (src: string): Promise<VideoMetadata> => {
    if (fileSourcePattern.test(src)) {
        const { width, height } = await readFileMetadata(src);

        return {
            title: '',
            thumbnailSrc: '',
            thumbnailHeight: 0,
            thumbnailWidth: 0,
            aspectRatio: height / width,
        };
    }

    let request = requestCache[src];

    if (!request) {
        request = fetch(`https://noembed.com/embed?url=${src}`).then((r) => r.json());
        requestCache[src] = request;
    }

    const { title, thumbnail_url, thumbnail_height, thumbnail_width, width, height } =
        await request;

    return {
        title: title ?? '',
        thumbnailSrc: thumbnail_url,
        thumbnailHeight: thumbnail_height,
        thumbnailWidth: thumbnail_width,
        aspectRatio: height / width,
    };
};

export const useVideoMetadata = (url: string): UseVideoMetadata => {
    const [metadata, setMetadata] = useState<VideoMetadata | null>(null);
    const [loading, setLoading] = useState(true);

    const updateMetadata = async () => {
        setLoading(true);
        await fetchVideoMetadata(url).then(setMetadata);
        setLoading(false);
    };

    useEffect(() => {
        updateMetadata();
    }, [url]);

    return {
        metadata,
        loading,
    };
};
